import axios from "axios";
import store from "redux/store";
import { DateTime } from "luxon";
import LatLon from "geodesy/latlon-spherical.js";

//leaflet
import L from "leaflet";
import "leaflet-arc";
import "lib/MovingMarker";
import "leaflet-rotatedmarker";

// custom hooks and utilities
import { getAirportById } from "hooks/useAirport";
import { getModelData } from "hooks/useModelData";
import calcFlightTime from "common/calcFlightTime";
import importAll from "common/importAll";
import getMap from "common/getMap";

// classes
import FlightPlan from "./flightPlan";
import Statistics from "./statistics";
import { Pilot, Staff } from "./Staff";
import Revenue from "./revenue";
import Flight from "./flight";
import Route from "./route";
import Slot from "./slot";

// api
import api from "api/api";

// assets
import manufactureList from "assets/json/manufactureList.json";
import otherManufactureList from "assets/json/otherManufactureList.json";
import { transaction } from "common/money";
import calcDistance from "common/calcDistance";

const allMarkers = importAll(require.context("assets/images/map/markers", true, /\.(png|jpe?g|svg)$/));

class Aircraft {
	ident;

	grounded;
	assignedFlightPlan;
	currentFlight;
	route;

	departed;
	departedFrom;
	location;
	departure;

	flightsSinceCleaning;
	registration;
	delivered;
	earned;
	cycles;
	specs;
	id;

	_clicked;
	_showedInPopup;

	constructor(obj) {
		delete obj.clicked;
		delete obj._clicked;
		delete obj.showedInPopup;
		Object.assign(this, obj);
		this.depart = this.depart.bind(this);
		this.onFlightEnd = this.onFlightEnd.bind(this);
		this.onCleaningEnd = this.onCleaningEnd.bind(this);
		this.flightsSinceCleaning ??= 0;

		const currentlyInPopup = store.getState().sideWindow.currentAddress.split("~")[1];
		this.showedInPopup = currentlyInPopup === this.ident;
		store.subscribe(() => {
			const state = store.getState();
			const newClickedAircraft = +state.sideWindow.currentAddress.split("~")[1];
			let somethingChanged = false;

			// showing
			if (newClickedAircraft === this.ident && !this.showedInPopup) {
				this.showedInPopup = true;
				somethingChanged = true;
			}

			// hiding
			if (newClickedAircraft !== this.ident && this.showedInPopup) {
				this.showedInPopup = false;
				somethingChanged = true;
			}

			if (somethingChanged) {
				window.aircraftPopupTimeouts ??= [];
				window.aircraftPopupTimeouts.forEach(timeout => clearTimeout(timeout));
				window.aircraftPopupTimeouts = [];
			}
		});
	}

	init() {
		this.checkRoutes();

		if (this.status === "inflight") {
			const route = Route.get(this.route);
			const flightTime = ((route.distance / this.specs.speed[0]) * 3600000) / 1.5; // milliseconds
			const landTime = this.departed * 1000 + flightTime;
			const toLanding = landTime - DateTime.utc().toMillis();

			if (toLanding <= 0) {
				this.onFlightEnd(route.distance, toLanding < -60 ? 0 : 60 - Math.abs(toLanding));
				return;
			}

			this.drawMovingMarker();
			return;
		}

		if (!this.location && this.route) {
			this.drawMovingMarker();
			return;
		}

		if (this.status === "delivery") {
			const now = Math.round(DateTime.utc().toMillis());
			const timeToDeparture = this.departure + this.ordered * 1000 - now;

			if (timeToDeparture < 0) {
				this.drawMovingMarker();
				return;
			}

			this.scheduleDeparture(timeToDeparture);
		}

		if (this.status === "cleaning") {
			const remaining = this.getRemainingCleaningTime();

			if (remaining > 0) {
				setTimeout(this.onCleaningEnd, remaining * 1000);
			} else {
				this.onCleaningEnd();
			}
		}

		if (this.status === "parked" && this.route) Route.get(this.route).removeAircraft(this.ident);
		this.drawStaticMarker();

		document.addEventListener("depart", e => {
			e.stopImmediatePropagation();
			if (this.ident !== e.detail.aircraft.ident) return;
			this.depart();
		});

		return this;
	}

	// getters & setters
	get clicked() {
		return this._clicked;
	}

	set clicked(val) {
		this._clicked = val;
		this.updateIcon();
	}

	get showedInPopup() {
		return this._showedInPopup;
	}

	set showedInPopup(val) {
		const wasJustShown = !this._showedInPopup && val;
		const wasJustHidden = this._showedInPopup && !val;
		this.clicked = val;
		this._showedInPopup = val;

		const currentlyInPopup = +store.getState().sideWindow.currentAddress.split("~")[1] ?? null;
		if (wasJustHidden && [null, this.ident].includes(currentlyInPopup)) {
			store.dispatch({ type: "SET_SIDE_WINDOW_ADDRESS", state: "" });
		}

		if (wasJustShown) {
			store.getState().fleet?.forEach(a => a.ident !== this.ident && (a.showedInPopup = false));
			store.dispatch({ type: "SET_SIDE_WINDOW_ADDRESS", state: `aircraft~${this.ident}` });
			store.dispatch({ type: "AIRCRAFT_POPUP_OPEN" });
		}
	}

	/**
	 * @returns {Array<Array<string, string, string>>} Array of labels (pl & en & icon class)
	 */
	get requiredPilotCrew() {
		const modelData = getModelData(this.id);
		const requiredPilots = modelData.crew.pilots;
		const arr = [];

		for (let i = 0; i < requiredPilots; i++) {
			arr.push(Pilot.workerPosition(6 - requiredPilots + i));
		}

		arr.reverse();
		return arr;
	}

	/**
	 * @return {number}
	 */
	get requiredCabinCrew() {
		const modelData = getModelData(this.id);
		return modelData.crew.cabin_crew;
	}

	/**
	 * @return {number}
	 */
	get missingCrewCount() {
		const assignedCount = Staff.getAssignedWorkers(this.ident).length;
		return this.requiredPilotCrew.length + this.requiredCabinCrew - assignedCount;
	}

	// drawing and removing markers
	/**
	 * A method that draws a static marker.
	 * @return {Promise<void>}
	 * @async
	 */
	async drawStaticMarker() {
		const airportList = store.getState().airports;
		const map = await getMap();

		if (this.parkedMarker || !this.location || !this.id || !map || this.movingMarkerExists()) return;

		const location = getAirportById(airportList, this.location);
		const aircraftMarker = L.marker([location.latitude_deg, location.longitude_deg], {
			icon: this.getMarkerIcon(),
			rotationAngle: Math.round(Math.random() * 360),
			aircraftIdent: this.ident,
			type: "static"
		}).addTo(map);

		this.parkedMarker = aircraftMarker;

		addMultipleEventListeners(aircraftMarker, "mouseover", () => (this.clicked = true));

		addMultipleEventListeners(aircraftMarker, "mouseout", () => {
			if (this.showedInPopup) return;
			this.clicked = false;
		});

		addMultipleEventListeners(aircraftMarker, "click", () => {
			this.showedInPopup = !this.showedInPopup;
		});

		document.addEventListener("updateAircraftIcon", e => {
			if (e.detail.aircraftIdent !== this.ident) return;
			aircraftMarker.setIcon(this.getMarkerIcon());
		});
	}

	/**
	 * A method that draws a moving marker.
	 * @return {Promise<void>}
	 * @async
	 */
	async drawMovingMarker() {
		if (!this.route) {
			this.drawStaticMarker();
			return;
		}

		const map = await getMap();
		const route = Route.get(this.route);
		const now = Math.round(DateTime.utc().toMillis());
		const timeToDeparture = this.departure + this.ordered * 1000 - now;
		const shouldHaveBeenDeparted = timeToDeparture < 0;

		if (!this.departed && this.status !== "delivery") return;
		if (this.status === "delivery" && !shouldHaveBeenDeparted) {
			this.scheduleDeparture(timeToDeparture);
			return;
		}

		if (!route) {
			console.error("Route is undefined!");
			return;
		}

		const flightTime = ((route.distance / this.specs.speed[0]) * 3600000) / 1.5; // milliseconds
		let [plane1LatLngs, plane2LatLngs] = await route.getLatLngs();
		let remainingFlightTime = flightTime;
		if (this.departedFrom === route.to) {
			plane1LatLngs.reverse();
			plane2LatLngs.reverse();
		}

		if (!this.departed && shouldHaveBeenDeparted && this.status === "delivery") {
			this.departed = this.ordered + this.departure / 1000; // seconds

			this.location = null;
			this.departure = null;
		}

		if (this.departed && !this._departedInThisSession) {
			this._departedInThisSession = true;
			const now = Math.round(DateTime.utc().toSeconds());
			const timeElapsed = now - this.departed; // sec

			// get flight progress and get corresponding latlngs
			const aircraftSpeed = this.specs.speed[0] * 1.5; //km / h
			const traveledDistance = aircraftSpeed * (timeElapsed / 3600);
			const traveledProgress = 100 / (route.distance / traveledDistance);
			const latlngsLength = route.polylines[0]._latlngs.length;

			let index = Math.round(traveledProgress * (latlngsLength / 100) - 1); //\\  * (latlngsLength / 100) because x vertices  //\\
			index = index > latlngsLength - 2 ? latlngsLength - 2 : index;
			plane1LatLngs = plane1LatLngs.slice(index);
			plane2LatLngs = plane2LatLngs.slice(index);
			remainingFlightTime = flightTime - timeElapsed * 1000; // milliseconds
		}

		if (!this.departed || this.markers) return;
		if (this.movingMarkerExists()) return;

		const { path: iconPath, size: iconSize } = getModelData(this.id).icon;
		const aircraftState = this.clicked ? "clicked" : "normal";
		const plane1 = L.Marker.movingMarker(plane1LatLngs, remainingFlightTime, {
			icon: this.getMarkerIcon(this.status, aircraftState, iconPath, iconSize),
			rotationAngle: 45,
			autostart: true,
			aircraftIdent: this.ident,
			type: "moving"
		}).addTo(map);

		const plane2 = L.Marker.movingMarker(plane2LatLngs, remainingFlightTime, {
			icon: this.getMarkerIcon(this.status, aircraftState, iconPath, iconSize),
			rotationAngle: 45,
			autostart: true,
			aircraftIdent: this.ident,
			type: "moving"
		}).addTo(map);

		if (plane1 == null || plane2 == null) return;

		//rotation
		const interval = setInterval(() => {
			if (plane1 == null) {
				console.log("clearing");
				clearInterval(interval);
				return;
			}

			const p1 = plane1.getLatLng();
			let p2;
			let closestDistance = Infinity;

			plane1LatLngs.forEach(({ lat, lng }, i, a) => {
				const calculated = calcDistance(p1.lat, p1.lng, lat, lng);
				if (i === a.length - 1) {
					clearInterval(interval);
					return false;
				}

				if (calculated < closestDistance) {
					closestDistance = calculated;
					p2 = a[i + 1];
					return false;
				}
			});

			const angleDeg = calculateHeading(p1.lat, p1.lng, p2.lat, p2.lng);
			plane1.setRotationAngle(angleDeg);
		}, 200);

		const interval2 = setInterval(() => {
			if (plane2 == null) {
				clearInterval(interval2);
				return;
			}

			const p1 = plane2.getLatLng();
			let p2;
			let closestDistance = Infinity;

			plane2LatLngs.forEach(({ lat, lng }, i, a) => {
				const calculated = calcDistance(p1.lat, p1.lng, lat, lng);
				if (i === a.length - 1) {
					clearInterval(interval2);
					return false;
				}

				if (calculated < closestDistance) {
					closestDistance = calculated;
					p2 = a[i + 1];
					return false;
				}
			});

			const angleDeg = calculateHeading(p1.lat, p1.lng, p2.lat, p2.lng);
			plane2.setRotationAngle(angleDeg);
		}, 200);

		//marker event listeners
		[plane1, plane2].forEach(el => {
			el.addEventListener("end", e => {
				if (!store.getState().isLoggedIn) return;
				e.target.setIcon(this.getMarkerIcon(this.getNewStatus()));
			});

			addMultipleEventListeners(el, "mouseover", () => (this.clicked = true));

			addMultipleEventListeners(el, "mouseout", () => {
				if (this.showedInPopup) return;
				this.clicked = false;
			});

			addMultipleEventListeners(el, "click", () => {
				this.showedInPopup = !this.showedInPopup;
			});
		});

		if (this.status !== "delivery") {
			plane1.addEventListener("end", ({ elapsedTime }) => this.onFlightEnd(null, 60 - elapsedTime / 1000));
		}

		//update the aircraft and routes and remove lines
		if (this.status === "delivery") {
			const now = DateTime.utc();
			const speed = +this.specs.speed[0];
			const flightTime = (route.distance / speed / 1.5) * 3600000; // milliseconds
			const willBeDelivered = this.departed * 1000 + flightTime - now;
			setTimeout(this.onFlightEnd, willBeDelivered);
		}

		this.markers = [plane1, plane2];
		this.updateInRedux();
		// Aircraft.saveFleet();
	}

	/**
	 * A method that removes all aircraft's markers.
	 * @return {Promise<void>}
	 * @async
	 */
	async removeFromMap() {
		const map = await getMap();
		if (this.markers && !this.markers.includes(null)) this.markers.forEach(m => m.removeFrom(map));
		if (this.parkedMarker) this.removeStaticMarker(this.ident);
	}

	/**
	 * A method that removes a static marker.
	 * @return {Promise<void>}
	 * @async
	 */
	async removeStaticMarker() {
		const map = await getMap();

		if (!this.parkedMarker) {
			const found = Object.values(map._layers).find(
				({ options = {} }) => options.aircraftIdent === this.ident && options.type === "static"
			);

			if (found) {
				found.removeFrom(map);
				this.parkedMarker = null;
				return;
			}
			console.error(`Cannot remove a static marker for "${this.ident}", because it is undefined!`);
			return;
		}

		this.parkedMarker.removeFrom(map);
		this.parkedMarker = null;
	}

	/**
	 * Departs an aircraft.
	 * @param {boolean} displayRevenue
	 * @param {boolean} save
	 * @returns {Promise<void>} A promise.
	 */
	async depart() {
		if (this.status === "delivery") {
			console.error(
				"Called the depart() method on an aircraft in delivery. Please use departAircraftInDelivery() instead."
			);
			return;
		}

		if (this._departedInThisSession || this.grounded || this.status === "cleaning" || !this.route) return;
		if (store.getState().groupInfo.fuelSupplier == null) {
			return alert(
				store.getState().lang === "PL"
					? "By odprawić samolot musisz wcześniej podpisać kontrakt z dostawcą paliwa!"
					: "To depart an aircraft, you have to sign a contract with a fuel supplier!"
			);
		}

		if (this.missingCrewCount > 0) {
			return void alert(
				store.getState().lang === "PL"
					? "By odprawić samolot załoga musi być kompletna!"
					: "To depart an aircraft, crew must be complete!"
			);
		}

		var revenue = await this.realDepart();
		const amount = revenue.total;
		Statistics.addPassengersTravelled(revenue.passengers);
		revenue.showInPopup();

		await transaction(amount > 0 ? "add" : "subtract", Math.abs(amount), 4);

		store.dispatch({ type: "AIRCRAFT_UPDATE", payload: this });
		Aircraft.saveFleet();

		return revenue;
	}

	async departAircraftInDelivery() {
		await this.realDepart();
		store.dispatch({ type: "AIRCRAFT_UPDATE", payload: this });
		Aircraft.saveFleet();
	}

	async realDepart() {
		if (this._departedInThisSession || this.grounded || this.status === "cleaning" || !this.route) return;
		if (store.getState().groupInfo.fuelSupplier == null && this.status !== "delivery") return;
		if (this.status !== "delivery" && this.missingCrewCount > 0) return;

		await this.removeStaticMarker();
		this.departed = Math.round(DateTime.utc().toSeconds());
		this._departedInThisSession = true;
		this.departedFrom = this.location;
		this.departure = null;
		this.location = null;
		this.status !== "delivery" && (this.status = this.getNewStatus());
		this.drawMovingMarker();

		if (this.status !== "delivery") {
			var revenue = new Revenue(this.ident, this.route, false);
			this.earned += revenue.total;
		}

		this.cycles += 0.5;
		store.dispatch({ type: "AIRCRAFT_UPDATE", payload: this });
		return revenue;
	}

	scheduleDeparture(timeToDeparture = null) {
		if (this.departed || !this.departure || this._scheduledDeparture) return;
		this._scheduledDeparture = true;

		if (!timeToDeparture) {
			const now = Math.round(DateTime.utc().toMillis());
			timeToDeparture = this.departure + this.ordered * 1000 - now;
		}

		setTimeout(this.departAircraftInDelivery.bind(this), timeToDeparture < 1 ? 1 : timeToDeparture);
	}

	/**
	 * A method that checks if multiple routes have an aircraft assigned and corrects them.
	 */
	checkRoutes() {
		// const allRoutes = store.getState().routes;
		// const routesWithAircraft = allRoutes.filter(r => r.id !== this.route && r.aircraft.includes(this.ident));
		// routesWithAircraft.forEach(r => r.removeAircraft(this.ident));
	}

	// events
	async onFlightEnd(distance = null, remainingCleaningTimeTime = 60) {
		if (!store.getState().isLoggedIn) return;
		await this.removeFromMap();
		const route = Route.get(this.route);

		// emit event if aircraft was in delivery
		if (this.status === "delivery") {
			const hub = store.getState().airlineInfo.hubs[0];
			this.location = hub;

			this.delivered = this.delivered || DateTime.utc().toSeconds();
			this.flightsSinceCleaning = 0;

			distance ??= route?.distance;
			route?.removeAircraft(this.ident);
			this.route = null;

			document.dispatchEvent(
				new CustomEvent("aircraftDelivered", {
					detail: { aircraftId: this.ident }
				})
			);
		} else {
			this.flightsSinceCleaning++;
		}

		// update aircraft
		this.location ??= this.departedFrom === route.to ? route.from : route.to;
		this.flightTime += calcFlightTime(route?.distance ?? distance, this.specs.speed[0]);
		this.flownDistance += route?.distance ?? distance;
		this._departedInThisSession = false;
		this.departedFrom = null;
		this.departed = null;
		this.markers = null;
		this.cycles += 0.5;

		const cleaningSettings = store.getState().airlineInfo.settings.cleaning;
		const needsCleaning = this.flightsSinceCleaning >= cleaningSettings && cleaningSettings > 0;

		if (needsCleaning && remainingCleaningTimeTime <= 0) {
			this.flightsSinceCleaning = 0;
			this.cleaningStarted = null;
		}

		if (needsCleaning && remainingCleaningTimeTime > 0) {
			await this.startCleaning(remainingCleaningTimeTime);
		} else {
			this.status = this.getNewStatus();
		}

		if (this.assignedFlightPlan && this.currentFlight && this.location === route.from) {
			[this.route, this.currentFlight] = await FlightPlan.get(this.assignedFlightPlan).getNextFlight(
				this.currentFlight,
				this
			);
		}

		store.dispatch({ type: "AIRCRAFT_UPDATE", payload: this });
		this.drawStaticMarker();
		Aircraft.saveFleet();

		this.checkRoutes();
	}

	async startCleaning(remainingCleaningTimeTime = 60) {
		this.status = "cleaning";
		this.cleaningStarted = DateTime.utc().toSeconds() - (60 - remainingCleaningTimeTime);

		setTimeout(this.onCleaningEnd, remainingCleaningTimeTime * 1000);
		const amount = Object.values(this.specs.capacity).reduce((acc, v) => v + acc, 0) * 15;
		await transaction("subtract", amount, 5);
	}

	updateIcon() {
		if (this.parkedMarker) this.parkedMarker.setIcon(this.getMarkerIcon());
		if (this.markers != null && !this.markers.includes(null)) {
			this.markers.forEach(m => m.setIcon(this.getMarkerIcon()));
		}
	}

	onCleaningEnd() {
		if (!store.getState().isLoggedIn) return;

		this.status = this.assignedFlightPlan && this.currentFlight ? "landed" : "parked";
		this.flightsSinceCleaning = 0;
		this.cleaningStarted = null;

		store.dispatch({ type: "AIRCRAFT_UPDATE", payload: this });
		Aircraft.saveFleet();
	}

	// other stuff
	getNewStatus() {
		switch (this.status) {
			case "delivery":
				return "parked";
			case "inflight":
				return "landed";
			case "landed":
				return "inflight";
			default:
				return this.status;
		}
	}

	getMarkerIcon(s = null) {
		const { path: iconPath, size: iconSize } = getModelData(this.id).icon;
		const fraction = iconSize / 100;
		const size = 40 * fraction;
		const anchor = size / 2;

		if ([s, this.status].includes("cleaning")) s = "landed";

		return new L.Icon({
			iconUrl: getAircraftIcon(s || this.status, this.clicked ? "clicked" : "normal", iconPath),
			iconSize: [size, size],
			iconAnchor: [anchor, anchor]
		});
	}

	movingMarkerExists() {
		return Object.values(window.map._layers)
			.map(({ options = {} }) => (!options.aircraftIdent ? null : options.aircraftIdent === this.ident))
			.includes(true);
	}

	/**
	 * @return {number} Remaining flight time in seconds. Throws if route is undefined.
	 */
	getRemainingFlightTime() {
		if (!["inflight", "delivery"].includes(this.status) || this.route == null) return 0;

		try {
			const flightTime = calcFlightTime(Route.get(this.route).distance, this.specs.speed[0]);
			if (!this.departed) return flightTime;

			let traveledTime = DateTime.utc().toSeconds() - this.departed;
			traveledTime < 0 && (traveledTime = 0);
			traveledTime > flightTime && (traveledTime = flightTime);
			return traveledTime > flightTime ? 0 : flightTime - traveledTime;
		} catch (err) {
			console.error(err);
			return 0;
		}
	}

	/**
	 * @param {number} distance Route distance
	 * @return {number} Remaining flight time in milliseconds
	 */
	getRemainingFlightTimeWithDistance(distance) {
		const now = Math.round(DateTime.utc().toSeconds()); // seconds
		const timeElapsed = (now - this.departed) * 1000; // ms
		const flightTime = ((distance / this.specs.speed[0]) * 3600000) / 1.5; // ms
		return flightTime - timeElapsed;
	}

	getRemainingBuildTime() {
		if (this.status !== "delivery" || this.departed) return 0;

		const now = DateTime.utc().toMillis();
		const remaining = (this.ordered * 1000 + this.departure - now) / 1000; // sec
		return remaining;
	}

	/**
	 * @return {number} Remaining cleaning time in seconds. Throws if route is undefined.
	 */
	getRemainingCleaningTime() {
		if (this.status !== "cleaning") return 0;
		const now = DateTime.utc().toSeconds();
		const finishTime = this.cleaningStarted + 60;
		const remaining = finishTime - now;

		return Math.round(remaining);
	}

	planCanBeChanged(showAlert = true) {
		const hubId = store.getState().airlineInfo.hubs[0];
		if (this.location !== hubId || this.status === "inflight") {
			showAlert &&
				alert(
					`By wykonać tą akcję, samolot musi być w bazie!\nTo preform this action, the aircraft must be at its base!`
				);
			return false;
		}

		return true;
	}

	/**
	 * Assigns a route to the aircraft by looking at the currentFlight property.
	 * @return {Aircraft} An Aircraft on which operations were preformed
	 */
	async assignRoute() {
		if (this.route) {
			Route.get(this.route).removeAircraft(this.ident);
			this.route = null;
		}

		if (this.status === "delivery") {
			const hub = store.getState().airlineInfo.hubs[0];

			// In theory this condition should be always false
			if (this.departedFrom == null) {
				const aircraftList = store.getState().aircraftList;
				const airportList = store.getState().airports;

				const manufacturer = Object.entries(aircraftList.passenger).reduce((acc, [manufacturer, models], i) => {
					if (i === 0) return acc;
					const model = models.find(m => m.id === this.id);
					return model ? (manufacturer === "Other" ? model.manufacturer : manufacturer) : acc;
				}, null);

				const manufacturerAircraftList = manufactureList[manufacturer] || otherManufactureList[manufacturer];
				this.departedFrom = Object.values(airportList)
					.map(country => Object.values(country))
					.flat()
					.find(a => a.gps_code === manufacturerAircraftList?.airportCode)?.id;
			}

			const route = Route.new(this.departedFrom, hub, {
				destinationPin: false,
				originPin: true,
				type: "delivery"
			});

			if (this.getRemainingFlightTimeWithDistance(route.distance) <= 0) {
				this.onFlightEnd(route.distance);
				return this;
			}

			await route.addAircraft(this.ident);
			this.route = route.id;
			this.updateInRedux();
			return this;
		}

		if (!this.currentFlight) return this;
		const flight = Flight.get(this.currentFlight);
		const slot = Slot.get(flight.slotId);

		const route = Route.new(flight.origin, slot.destination);
		await route.addAircraft(this.ident);
		this.route = route.id;
		return this;
	}

	async assignFlightPlan(flightPlanId) {
		try {
			const { data } = await axios.patch(`${api}/aircraft/${this.ident}/setFlightPlanId`, { flightPlanId });
			this.assignedFlightPlan = flightPlanId;
			this.currentFlight = !data && data !== 0 ? null : data;
			this.status = "landed";

			await this.assignRoute();
			this.updateInRedux();

			document.dispatchEvent(new CustomEvent("updateAircraftIcon", { detail: { aircraftIdent: this.ident } }));
		} catch (error) {
			if (error.status === 409) {
				const lang = store.getState().lang;
				const d = error.response.data;
				alert(
					lang === "PL"
						? `Samolot nie ma wystarczającego zasięgu. Wymagany zasięg to ${d} km`
						: `The selected aircraft has too low range. Required range is ${d} km`
				);
				return;
			}

			console.error(error);
		}
	}

	updateInRedux() {
		store.dispatch({ type: "AIRCRAFT_UPDATE", payload: this.ident });
	}

	// static methods
	/**
	 * A method used to get an aircraft based on its identifier
	 * @param {String} ident The identifier of the aircraft.
	 * @return {Aircraft | undefined} Returns the aircraft or undefined if not found.
	 */
	static get(ident) {
		return store.getState().fleet.get(ident);
	}

	static async deleteAll() {
		const map = await getMap();
		await Route.deleteAll();

		store.getState().fleet.forEach(aircraft => {
			if (aircraft.markers && !aircraft.markers.includes(null)) {
				aircraft.markers.forEach(marker => marker.removeFrom(map));
				return true;
			}

			aircraft.removeStaticMarker();

			if (aircraft.assignedFlightPlan) FlightPlan.get(aircraft.assignedFlightPlan).unassignAircraft(true);
		});

		try {
			await axios.delete(`${api}/aircraft`);
		} catch (e) {
			console.error(e);
			return;
		}

		store.dispatch({ type: "SET_FLEET", payload: new Map() });
	}

	static async createRoutes() {
		/**
		 * @type {Map<number, Aircraft>}
		 */
		const allAircraft = store.getState().fleet;
		for (const a of allAircraft.values()) {
			await a.assignRoute();
			a.init();
		}
	}

	/**
	 * A method that saves whole fleet to the db.
	 * @param {Array<Aircraft> | undefined} fleetToSave Fleet to be saved. Took form redux if undefined.
	 * @return {boolean} success.
	 * @static
	 * @async
	 */
	static async saveFleet(fleetToSave = [...store.getState().fleet.values()]) {
		fleetToSave = fleetToSave.map(aircraft => {
			if (aircraft instanceof Aircraft) {
				aircraft = aircraft.toObject();
			}

			return { ...aircraft, markers: null, parkedMarker: null };
		});

		try {
			await axios.put(`${api}/aircraft`, fleetToSave);
		} catch (e) {
			console.error(e);
			return false;
		}

		return true;
	}

	// object conversions
	/**
	 * A method used to convert a class instance into an object. Also outputs private properties!
	 * @return {Object} A class instance converted to an object
	 */
	toObject() {
		const keys = Object.getOwnPropertyNames(Object.getPrototypeOf(this));

		let instanceAsObject = keys.reduce((classAsObj, key) => {
			classAsObj[key] = this[key];
			return classAsObj;
		}, {});

		instanceAsObject = Object.keys(this).reduce((classAsObj, key) => {
			classAsObj[key] = this[key];
			return classAsObj;
		}, instanceAsObject);

		for (let key in instanceAsObject) {
			if (typeof instanceAsObject[key] === "function") delete instanceAsObject[key];
			if (key[0] === "_") delete instanceAsObject[key];
			if (key === "clicked") delete instanceAsObject[key];
		}

		delete instanceAsObject.toObject;
		delete instanceAsObject.constructor;
		delete instanceAsObject.route;
		return instanceAsObject;
	}

	/**
	 * A static method used to convert an object to an Aircraft
	 * @param {Object} obj The object to convert
	 * @return {Aircraft} The object converted to a class instance.
	 * @static
	 */
	static fromObject(obj) {
		return new Aircraft(obj);
	}
}

const addMultipleEventListeners = (el, s, fn) => s.split(" ").forEach(e => el.addEventListener(e, ev => fn(ev), false));
const getAircraftIcon = (status, state = "normal", aircraftMarkerId = "a320") => {
	status === "inflight" && (status = "defalut");
	const preferedMarker = allMarkers[`${aircraftMarkerId}/${aircraftMarkerId}-${status}-${state}.svg`];

	return preferedMarker || allMarkers[`a320/a320-defalut-normal.svg`];
};

function calculateHeading(lat1, lng1, lat2, lng2) {
	const currentLocation = new LatLon(lat1, lng1);
	const targetLocation = new LatLon(lat2, lng2);
	const heading = currentLocation.initialBearingTo(targetLocation);
	return heading;
}

export { Aircraft };
export default Aircraft;
